// src/lib/api/documentApi.ts
import { getApiClient } from './client';
import { ApiError, buildSearchParams } from 'jasit-api-kit';
import type { PaginationParams } from 'jasit-api-kit';
import CONFIG from '../config';

export interface Document {
    id: string;
    entity_type: string;
    entity_id: string;
    document_type: string;
    document_format: 'file' | 'text';
    file_url?: string;
    original_name?: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
    content?: string;
    created_at: string;
    updated_at: string;
}

export interface CreateTextDocumentRequest {
    entity_type: string;
    entity_id: string;
    document_type: string;
    content: string;
}

export interface DocumentsResponse {
    documents: Document[];
}

// Upload a file document
const uploadFileDocument = async (
    entityType: string,
    entityId: string,
    documentType: string,
    file: File
): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    formData.append('document_type', documentType);

    return getApiClient().uploadMultipart<Document>('/api/documents', formData, 'post');
};

// Create a text document
const createTextDocument = async (request: CreateTextDocumentRequest): Promise<Document> => {
    return getApiClient().post<Document>('/api/documents/text', request);
};

// Get documents for an entity
const getDocumentsByEntity = async (entityType: string, entityId: string): Promise<Document[]> => {
    try {
        const response = await getApiClient().get<DocumentsResponse>('/api/documents', {
            params: {
                entity_type: entityType,
                entity_id: entityId,
            },
        });
        return response.documents || [];
    } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
            return [];
        }
        throw error;
    }
};

// Delete a document
const deleteDocument = async (documentId: string): Promise<void> => {
    return getApiClient().delete<void>(`/api/documents/${documentId}`);
};

// Get download URL for a document.
//
// #356 — this route is AUTHENTICATED and owner-only now, so the URL cannot be
// put straight in an <a href>, <img src> or <iframe src>: a browser navigation
// sends no Authorization header and gets a 401. Fetch it with the bearer token
// and hand the browser a blob: URL (see PdfViewerModal).
//
// It also only serves the CALLER'S OWN documents. An employer reading an
// applicant's CV goes through /api/applications/:id/documents/:documentId/download
// instead — that URL arrives on the applicant-profile response, so nothing on
// the employer side needs to build one.
const getDocumentDownloadUrl = (documentId: string): string => {
    return `${CONFIG.API_BASE_URL}${CONFIG.API_AUTH_PORT}/api/documents/${documentId}/download`;
};

// List all documents (admin function)
const listDocuments = async (pagination?: PaginationParams): Promise<Document[]> => {
    const params = pagination ? buildSearchParams({ ...pagination }) : undefined;
    const response = await getApiClient().get<DocumentsResponse>('/api/documents/list', { params });
    return response.documents || [];
};

export const documentApi = {
    uploadFileDocument,
    createTextDocument,
    getDocumentsByEntity,
    deleteDocument,
    getDocumentDownloadUrl,
    listDocuments,
};
